import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../../../../App";
import { ConvertMmToPx, formatDate } from "../../../../../hooks/Method";

const ThermalPrinterRecept = ({ data }) => {
  const { settings } = useContext(AppContext);
  const [store, setStore] = useState({});
  const { t } = useTranslation();

  useEffect(() => {
    setStore({ ...data.store });
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return data.id && store.id ? (
    <div
      style={{
        maxWidth: `${
          store.receiptWidth ? ConvertMmToPx(store.receiptWidth) : "220"
        }px`,
        width: "100%",
        fontFamily: "monospace",
        fontSize: "10px",
        padding: "15px 10px",
        lineHeight: 1.4,
        fontWeight: "600",
      }}
    >
      {/* Store Header */}
      <div
        style={{
          textAlign: "center",
          marginBottom: "15px",
          paddingBottom: "10px",
          borderBottom: "2px double #333",
        }}
      >
        {store.logo && store.showLogoOnReceipt === true && (
          <div
            style={{
              marginBottom: "10px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <img
              src={store.logo}
              alt="store_logo"
              style={{
                maxHeight: "60px",
                maxWidth: "120px",
                objectFit: "contain",
                filter: "grayscale(100%)",
              }}
            />
          </div>
        )}
        {store.showStoreNameOnReceipt && (
          <>
            <div
              style={{
                fontSize: "14px",
                fontWeight: "600",
                letterSpacing: "0.5px",
              }}
            >
              {store.name.name}
            </div>
            {store.name.localName && (
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  letterSpacing: "0.5px",
                  marginBottom: "4px",
                }}
              >
                {store.name.localName}
              </div>
            )}
          </>
        )}
        {(store.showAddressOnReceipt || store.showPhoneOnReceipt) && (
          <div
            style={{
              fontSize: "10px",
              color: "#00000",
              lineHeight: 1.3,
            }}
          >
            {store.showAddressOnReceipt && (
              <>
                {store.address && (
                  <span
                    style={{
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {store.address}
                  </span>
                )}
                <div>
                  {store.city ? `, ${store.city}` : ""}
                  {store.state ? `, ${store.state}` : ""}
                  {store.zip ? `, ${store.zip}` : ""}
                </div>
              </>
            )}
            <br />
            <div style={{ display: "flex", flexDirection: "column" }}>
              {store.showPhoneOnReceipt && store.phone && (
                <span>📞 {store.phone}</span>
              )}
              {store.showEmailOnReceipt && store.email && (
                <span>✉️ {store.email}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Receipt Title */}
      <div
        style={{
          display: "grid",
          textAlign: "center",
          fontWeight: "600",
          margin: "0 0 15px 0",
          color: "#333",
          textTransform: "uppercase",
          letterSpacing: "1px",
          borderBottom: "1px solid #ddd",
        }}
      >
        <span style={{ fontSize: "12px" }}>{t("quoteSaleReceipt")}</span>
        <span style={{ fontSize: "8px" }}>
          {t("status")} - {data.statusName}
        </span>
      </div>

      {/* Transaction Details */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "8px",
          marginBottom: "15px",
        }}
      >
        <div>
          <div style={{ fontWeight: "600" }}>{t("id")}</div>
          <div>#{data.id}</div>
        </div>
        <div>
          <div style={{ fontWeight: "600" }}>{t("date")}</div>
          <div>{formatDate(data.quoteDate)}</div>
        </div>
        {data.customer && (
          <>
            <div>
              <div style={{ fontWeight: "600" }}>{t("customer")}</div>
              <div>{data.customer.fullName}</div>
            </div>
            <div>
              <div style={{ fontWeight: "600" }}>{t("phone")}</div>
              <div>{data.customer.phone}</div>
            </div>
          </>
        )}
      </div>

      {/* Items Table */}
      <div
        style={{
          marginBottom: "15px",
          borderBottom: "2px double #333",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "3fr 1fr 1.5fr",
            gap: "5px",
            fontWeight: "600",
            marginBottom: "8px",
          }}
        >
          <div>{t("itemName")}</div>
          <div style={{ textAlign: "center" }}>{t("qty")}</div>
          <div style={{ textAlign: "right" }}>{t("totalPrice")}</div>
        </div>

        {data.quoteItems?.map((item, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "3fr 1fr 1.5fr",
              gap: "5px",
              marginBottom: "6px",
            }}
          >
            <div>{item.product.label}</div>
            <div style={{ textAlign: "center" }}>{item.qty}</div>
            <div style={{ textAlign: "right" }}>
              {settings.curSymbol}{" "}
              {(item.qty * item.price).toFixed(settings.decimal)}
            </div>
          </div>
        ))}
      </div>

      {/* Totals Section */}
      <div
        style={{
          display: "grid",
          gap: "6px",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>{t("subtotal")}:</span>
          <span>
            {settings.curSymbol} {data.subtotal.toFixed(settings.decimal)}
          </span>
        </div>

        {data.tax > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>
              {t("tax")} ({data.taxPct}%):
            </span>
            <span>
              +{settings.curSymbol} {data.tax.toFixed(settings.decimal)}
            </span>
          </div>
        )}

        {data.disAmt > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>{t("discount")}:</span>
            <span>
              -{settings.curSymbol} {data.disAmt.toFixed(settings.decimal)}
            </span>
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontWeight: "600",
            paddingTop: "8px",
            borderTop: "1px solid #ddd",
          }}
        >
          <span>{t("toPay")}:</span>
          <span>
            {settings.curSymbol} {data.toPay.toFixed(settings.decimal)}
          </span>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          display: "grid",
          fontSize: "10px",
          color: "#00000",
          lineHeight: 1.3,
          borderTop: "2px double #333",
          paddingTop: "10px",
          whiteSpace: "pre-wrap",
          overflowWrap: "break-word",
          maxWidth: "100%",
        }}
      >
        {data.note && (
          <span
            style={{
              borderBottom: "1px solid #ddd",
              paddingBottom: "7px",
              marginBottom: "7px",
            }}
          >
            {data.note}
          </span>
        )}
        <span> {settings.salesTermForReceipt}</span>
      </div>
    </div>
  ) : null;
};

export default ThermalPrinterRecept;
